import { Dialog } from 'primereact/dialog';
import { FC, Suspense, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReportHubModalFilters from './ReportHubModalFilters';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectUser } from '../../../../redux/reducers/userSlice';
import { DropdownItemModel } from '../../../../pages/SingleUnit';
import { ReportData, ReportGroupBy, ReportOrderBy, ReportSubtype } from '../../../../models/Report';
import { reportSubtypeToString } from '../../../../businessLogicService/reportSubtypeToString';
import { Roles } from '../../../../models/User';
import { PreloadedQuery, useQueryLoader, usePreloadedQuery } from 'react-relay';
import { getStandardBySites } from '../../../../graphQLOperations/ReportHub';
import { ReportHubGetStandardsBySitesQuery } from '../../../../graphQLOperations/__generated__/ReportHubGetStandardsBySitesQuery.graphql';
import ReportHubFooter from './ReportHubFooter';
import { ThreeDotLoader } from '../../../Fallback/ThreeDotsLoader';
import { Class } from '../../../../models/Class';
import {
  ReportHubDiagnosticAssessmentTotalQuery,
  ReportHubDiagnosticAssessmentTotalQuery$variables,
} from '../../../../graphQLOperations/__generated__/ReportHubDiagnosticAssessmentTotalQuery.graphql';
import {
  ReportHubSkillAssessmentsTotalQuery,
  ReportHubSkillAssessmentsTotalQuery$variables,
} from '../../../../graphQLOperations/__generated__/ReportHubSkillAssessmentsTotalQuery.graphql';
import {
  ReportHubOutstandingAssessmentsTotalQuery,
  ReportHubOutstandingAssessmentsTotalQuery$variables,
} from '../../../../graphQLOperations/__generated__/ReportHubOutstandingAssessmentsTotalQuery.graphql';
import { useReportHubQueryLoader } from '../../../../custom-hooks/useReportHubQueryLoader';
import { loadReportHubQuery } from '../../../../businessLogicService/loadReportHubQuerys';
import { sortByKey } from '../../../../businessLogicService/sortCriteria';
import { selectedReportsFilter, setReportsFilter } from '../../../../redux/reducers/filterReportsSlice';
import { Site } from '../../../../models/Site';
import type { RowData, DiagnosticSkillPerformanceItem } from '../../../DiagnosticsReport/SkillPerformanceByClassTable';
import { DiagnosticReportByClass } from '../../../DiagnosticsReport/DiagnosticReportSkillByClass';
import { filterStateDiagnosticAssessmentsInitValue } from '../../../AssessmentHistory/models';
import { normalizeClassIds, normalizeSiteIds } from '../../../../businessLogicService/reportDiagnosticsHelper';
import ExportUnfinishedDiagnosticStudents from './ExportDiagnosticStudents';
import ExportDiagnosticSkillsCsv from './ExportDiagnosticSkillsCsv';
import { formatClassDisplayName } from '../../../../utils/formatClassDisplayName';
import { useMutation } from 'react-relay';
import { recordExperimentSpeculativeData } from '../../../../graphQLOperations/Experiments';
import type { ExperimentsRecordExperimentsSpeculativeDataMutation } from '../../../../graphQLOperations/__generated__/ExperimentsRecordExperimentsSpeculativeDataMutation.graphql';
import { getExperimentSpeculativeData } from '../../../../graphQLOperations/Experiments';
import type { ExperimentsGetSpeculativeDataQuery } from '../../../../graphQLOperations/__generated__/ExperimentsGetSpeculativeDataQuery.graphql';

// Keys the teacher's group-by/order-by preferences are stored under, locally and server-side.
const EXPERIMENT_FLAG_NAME = 'TEAMB-2471-v2-teacher-table-preferences';
const PREFERENCES_VARIANT_NAME = 'teacher-table-preferences';
const STORAGE_KEY = 'teacher-table-preferences';

// Type guard for preferences validation
interface ReportPreferences {
  groupedBy: number;
  orderBy: number;
  lastUpdated: string;
}

const isValidPreferences = (data: unknown): data is ReportPreferences => {
  if (typeof data !== 'object' || data === null) return false;

  const groupByValues = Object.values(ReportGroupBy) as number[];
  const orderByValues = Object.values(ReportOrderBy) as number[];

  return (
    'groupedBy' in data &&
    'orderBy' in data &&
    'lastUpdated' in data &&
    typeof data.groupedBy === 'number' &&
    typeof data.orderBy === 'number' &&
    typeof data.lastUpdated === 'string' &&
    groupByValues.includes(data.groupedBy) &&
    orderByValues.includes(data.orderBy)
  );
};

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  selectedReportSubtype: ReportSubtype | null;
  allDropdowns: AllDropdowns;
  setAllDropdowns: (allDropdowns: AllDropdowns) => void;
  allClassesData: Class[];
  isReduxFiltersUsed?: boolean;
  miamiBrand: boolean;
}

export interface AllDropdowns {
  classes: DropdownItemModel[];
  roadmaps: DropdownItemModel[];
  sites: DropdownItemModel[];
}

// Helper component to sync preferences from server (runs once on modal open)
const PreferencesLoader: FC<{
  queryRef: PreloadedQuery<ExperimentsGetSpeculativeDataQuery>;
  onPreferencesLoaded: (groupedBy: ReportGroupBy, orderBy: ReportOrderBy) => void;
}> = ({ queryRef, onPreferencesLoaded }) => {
  const speculativeDataQuery = usePreloadedQuery(getExperimentSpeculativeData, queryRef);
  const hasAppliedPreferences = useRef(false);

  useEffect(() => {
    // Only apply server preferences once to prevent race conditions
    if (hasAppliedPreferences.current) return;

    try {
      const speculativeData = speculativeDataQuery?.userByPublicUserId?.experimentsSpeculativeData;

      if (speculativeData && speculativeData.length > 0) {
        const savedData = speculativeData.find(
          data => data?.flagName === EXPERIMENT_FLAG_NAME && data?.variantName === PREFERENCES_VARIANT_NAME,
        );

        if (savedData?.payload?.rootElement) {
          const payloadRoot = savedData.payload.rootElement;
          const payload = typeof payloadRoot === 'string' ? JSON.parse(payloadRoot) : payloadRoot;

          if (payload?.preferences && isValidPreferences(payload.preferences)) {
            const serverPreferences = payload.preferences;
            const localPrefsString = localStorage.getItem(STORAGE_KEY);
            const localPreferences = localPrefsString ? JSON.parse(localPrefsString) : null;

            // Only update if server has newer data than localStorage
            const shouldUpdate =
              !localPreferences ||
              !localPreferences.lastUpdated ||
              serverPreferences.lastUpdated > localPreferences.lastUpdated;

            if (shouldUpdate) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(serverPreferences));
              onPreferencesLoaded(serverPreferences.groupedBy, serverPreferences.orderBy);
              hasAppliedPreferences.current = true;
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to sync server preferences:', error);
    }
  }, [speculativeDataQuery, onPreferencesLoaded]);

  return null;
};

const ReportHubModal: FC<Props> = ({
  visible,
  setVisible,
  selectedReportSubtype,
  allDropdowns,
  setAllDropdowns,
  allClassesData,
  isReduxFiltersUsed = false,
  miamiBrand,
}) => {
  const dispatch = useAppDispatch();
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const userRedux = useAppSelector(selectUser);
  const { title } = reportSubtypeToString(selectedReportSubtype as ReportSubtype, miamiBrand);
  const isAdmin =
    userRedux?.roles?.some(role => role?.name === Roles.NETWORK_ADMIN || role?.name === Roles.SITE_ADMIN) ?? false;

  const isDiagnosticSkillsReport = selectedReportSubtype === ReportSubtype.DIAGNOSTIC_SUMMARY;
  const [values, setValues] = useState<ReportData>({
    roadmap: '',
    classes: [],
    sites: '',
    standards: '',
    groupedBy: ReportGroupBy.UNIT,
    orderBy: ReportOrderBy.ROWS,
  });
  const [standards, setStandards] = useState<DropdownItemModel[]>([]);
  const [triggerQueryKey, setTriggerQueryKey] = useState<number | null>(null);
  const { queryRef, loadQuery } = useReportHubQueryLoader(selectedReportSubtype as ReportSubtype);
  const [queryStandardsReference, loadStandardsQuery] =
    useQueryLoader<ReportHubGetStandardsBySitesQuery>(getStandardBySites);

  const [speculativeDataQueryRef, loadSpeculativeDataQuery] =
    useQueryLoader<ExperimentsGetSpeculativeDataQuery>(getExperimentSpeculativeData);

  const [preferencesLoaded, setPreferencesLoaded] = useState<boolean>(false);
  const hasLoadedServerPreferences = useRef<boolean>(false);

  const [dgSkillAssessments, setDgSkillAssessments] = useState<DiagnosticSkillPerformanceItem[]>([]);
  const [dgPlacedOutMap, setDgPlacedOutMap] = useState<
    Record<string, { id: string; firstName: string; lastName: string; publicUserId: string }[]>
  >({});
  const [dgClassStudents, setDgClassStudents] = useState<
    { id: string; firstName: string; lastName: string; publicUserId: string }[]
  >([]);
  const [dgLoading, setDgLoading] = useState<boolean>(false);

  const classIdsForFetcher = useMemo(() => normalizeClassIds(values.classes), [values.classes]);
  const siteIds = useMemo(() => normalizeSiteIds(values.sites), [values.sites]);
  const roadmapId = useMemo(() => (values.roadmap ? String(values.roadmap) : ''), [values.roadmap]);
  const roadmapIdsForFetcher = useMemo(() => (roadmapId ? [roadmapId] : []), [roadmapId]);
  const dgServerKey = useMemo(
    () => JSON.stringify({ roadmaps: roadmapIdsForFetcher, classes: classIdsForFetcher }),
    [roadmapIdsForFetcher, classIdsForFetcher],
  );
  const [commitSpeculativeData] = useMutation<ExperimentsRecordExperimentsSpeculativeDataMutation>(
    recordExperimentSpeculativeData,
  );

  useEffect(() => {
    setDgSkillAssessments([]);
    setDgPlacedOutMap({});
    setDgClassStudents([]);
    setDgLoading(true);
  }, [dgServerKey]);

  const dgFilterState = useMemo(() => {
    return {
      ...filterStateDiagnosticAssessmentsInitValue,
      filters: {
        ...filterStateDiagnosticAssessmentsInitValue.filters,
        classes: classIdsForFetcher,
        roadmaps: roadmapIdsForFetcher,
        skillName: '',
      },
    };
  }, [classIdsForFetcher, roadmapIdsForFetcher]);

  const rowsForCsv: RowData[] = useMemo(() => {
    const totalStudents = dgClassStudents.length;
    return dgSkillAssessments
      .filter(s => s.skill !== null)
      .map<RowData>(s => {
        const id = s.skill!.id;
        const placed = dgPlacedOutMap[id]?.length ?? s.placedOut?.length ?? 0;
        const notPlaced = Math.max(0, totalStudents - placed);
        return {
          skillId: id,
          skillName: s.skill?.name || '',
          type: s.skill?.displayType || '',
          unit: s.skill?.displayUnit || '',
          moreCount: s.skill?.moreCount ?? 0,
          allUnitsByType: s.skill?.allUnitsByType,
          PLACED_OUT: placed,
          NOT_PLACED_OUT: notPlaced,
        };
      });
  }, [dgSkillAssessments, dgPlacedOutMap, dgClassStudents]);

  const [startUnfinishedKey, setStartUnfinishedKey] = useState<number | null>(null);
  const [startSkillsKey, setStartSkillsKey] = useState<number | null>(null);

  // Track analytics and save preferences
  const savePreferencesAndTrack = useCallback(() => {
    if (!userRedux?.id) return;

    // Save preferences (localStorage + server)
    const preferences: ReportPreferences = {
      groupedBy: values.groupedBy,
      orderBy: values.orderBy,
      lastUpdated: new Date().toISOString(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save preferences to localStorage:', error);
    }

    const baseInput = {
      userId: `UserNode:${userRedux.id}`,
      flagName: EXPERIMENT_FLAG_NAME,
    };

    commitSpeculativeData({
      variables: {
        input: {
          input: {
            ...baseInput,
            variantName: PREFERENCES_VARIANT_NAME,
            payload: JSON.stringify({ preferences }),
          },
        },
      },
      onError: error => console.error('Failed to save preferences to server:', error),
    });
  }, [userRedux?.id, values.groupedBy, values.orderBy, commitSpeculativeData]);

  const triggerDownload = () => {
    setTriggerQueryKey(Math.random());
    savePreferencesAndTrack();

    if (selectedReportSubtype === ReportSubtype.DIAGNOSTIC_STUDENTS_UNFINISHED_DIAGNOSTIC) {
      if (!roadmapId || classIdsForFetcher.length === 0) return;
      setStartUnfinishedKey(Math.random());
      return;
    }
    if (isDiagnosticSkillsReport) {
      if (!!roadmapId && classIdsForFetcher.length > 0) {
        if (dgLoading) return;
        if (rowsForCsv.length) setStartSkillsKey(Math.random());
        return;
      }
    }
    if (selectedReportSubtype !== ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT) {
      loadReportHubQuery(
        selectedReportSubtype as ReportSubtype,
        loadQuery as (
          variables:
            | ReportHubDiagnosticAssessmentTotalQuery$variables
            | ReportHubSkillAssessmentsTotalQuery$variables
            | ReportHubOutstandingAssessmentsTotalQuery$variables,
        ) => void,
        values,
      );
    }
  };

  useEffect(() => {
    if (isAdmin) {
      if (siteIds.length > 0) {
        const classesBySites = allClassesData.filter(cls => {
          const siteId = cls.site?.id ? String(cls.site.id) : '';
          return siteId ? siteIds.includes(siteId) : false;
        });
        const classOptions =
          classesBySites
            .map(cls => {
              const classDisplay = formatClassDisplayName(cls);
              return {
                value: cls.id,
                label: `${classDisplay}`,
              };
            })
            .sort(sortByKey('label')) || [];
        setAllDropdowns({
          ...allDropdowns,
          classes: classOptions,
        });
      } else {
        setAllDropdowns({ ...allDropdowns, classes: [] });
        setStandards([]);
      }
    }

    if (selectedReportSubtype === ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT && siteIds.length > 0) {
      loadStandardsQuery({ sitesFilter: { siteIds } }, { fetchPolicy: 'network-only' });
    }
  }, [siteIds]);

  useEffect(() => {
    const sitesData = userRedux?.sites?.map(site => ({ value: site.id, label: site.name })) || [];
    if (sitesData.length === 1) {
      setValues(prev => ({ ...prev, sites: sitesData[0].value }));
      if (isReduxFiltersUsed && userRedux?.sites) {
        dispatch(setReportsFilter({ ...reportsFilterRedux, sites: userRedux?.sites[0] as Site }));
      }
    }
  }, [allClassesData]);

  // Callback to apply loaded preferences
  const handlePreferencesLoaded = useCallback((groupedBy?: ReportGroupBy, orderBy?: ReportOrderBy) => {
    setValues(prev => ({
      ...prev,
      ...(groupedBy !== undefined && { groupedBy }),
      ...(orderBy !== undefined && { orderBy }),
    }));
  }, []);

  // Load preferences from localStorage when modal opens
  useEffect(() => {
    if (visible) {
      try {
        const savedPreferences = localStorage.getItem(STORAGE_KEY);
        if (savedPreferences) {
          const preferences = JSON.parse(savedPreferences);
          if (isValidPreferences(preferences)) {
            handlePreferencesLoaded(preferences.groupedBy, preferences.orderBy);
          }
        }
      } catch (error) {
        console.error('Failed to load preferences from localStorage:', error);
      }

      if (userRedux?.sub && !hasLoadedServerPreferences.current) {
        hasLoadedServerPreferences.current = true;
        loadSpeculativeDataQuery({ userId: userRedux.sub }, { fetchPolicy: 'network-only' });
      }

      setPreferencesLoaded(true);
    } else {
      // Reset state when modal closes
      setPreferencesLoaded(false);
      hasLoadedServerPreferences.current = false;
      setValues({
        roadmap: '',
        classes: [],
        sites: '',
        standards: '',
        groupedBy: ReportGroupBy.UNIT,
        orderBy: ReportOrderBy.ROWS,
      });
    }
  }, [visible, userRedux?.sub, loadSpeculativeDataQuery, handlePreferencesLoaded]);

  const hasRoadmap = Boolean(roadmapId);

  return (
    <Suspense fallback={<ThreeDotLoader />}>
      {speculativeDataQueryRef && (
        <PreferencesLoader queryRef={speculativeDataQueryRef} onPreferencesLoaded={handlePreferencesLoaded} />
      )}
      <Dialog
        header={<h3 className="md:text-2xl text-lg">Generate Report</h3>}
        visible={visible}
        style={{
          width: '35vw',
          height: '75vh',
          maxHeight: selectedReportSubtype === ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT ? '80%' : '',
        }}
        breakpoints={{ '960px': '75vw', '576px': '100vw' }}
        onHide={() => setVisible(false)}
        footer={
          <ReportHubFooter
            values={values}
            triggerDownload={triggerDownload}
            selectedReportSubtype={selectedReportSubtype}
            closeModalHandle={() => setVisible(false)}
            triggerQueryKey={triggerQueryKey}
            queryRef={
              queryRef as
                | PreloadedQuery<ReportHubDiagnosticAssessmentTotalQuery, Record<string, unknown>>
                | PreloadedQuery<ReportHubSkillAssessmentsTotalQuery, Record<string, unknown>>
                | PreloadedQuery<ReportHubOutstandingAssessmentsTotalQuery, Record<string, unknown>>
            }
            isReduxFiltersUsed={!!isReduxFiltersUsed}
          />
        }
      >
        <p style={{ fontFamily: 'Poppins' }} className="md:text-base">
          To generate <span className="font-bold">{title} Report</span>, please use the filters below:
        </p>
        {preferencesLoaded ? (
          <Suspense fallback={<ThreeDotLoader />}>
            <ReportHubModalFilters
              values={values}
              setValues={setValues}
              allDropdowns={allDropdowns}
              standards={standards}
              setStandards={setStandards}
              selectedReportSubtype={selectedReportSubtype}
              queryStandardsReference={
                queryStandardsReference as PreloadedQuery<ReportHubGetStandardsBySitesQuery, Record<string, unknown>>
              }
              isAdmin={isAdmin}
              isReduxFiltersUsed={!!isReduxFiltersUsed}
            />
          </Suspense>
        ) : (
          <ThreeDotLoader />
        )}
        {hasRoadmap && isDiagnosticSkillsReport && (
          <Suspense fallback={null}>
            <DiagnosticReportByClass
              key={dgServerKey}
              filterState={dgFilterState}
              setSkillAssessments={setDgSkillAssessments}
              setPlacedOutMap={setDgPlacedOutMap}
              setClassStudents={setDgClassStudents}
              setLoading={setDgLoading}
            />
          </Suspense>
        )}
        <ExportUnfinishedDiagnosticStudents
          start={startUnfinishedKey}
          roadmapId={roadmapId}
          classIds={classIdsForFetcher}
          onDone={() => {
            setStartUnfinishedKey(null);
            setTriggerQueryKey(Math.random());
            setVisible(false);
          }}
        />
        {isDiagnosticSkillsReport && (
          <ExportDiagnosticSkillsCsv
            start={startSkillsKey}
            rows={rowsForCsv}
            loading={dgLoading}
            onDone={() => {
              setStartSkillsKey(null);
              setVisible(false);
            }}
          />
        )}
      </Dialog>
    </Suspense>
  );
};
export default ReportHubModal;
